import React, { Component } from 'react';

import { captureException } from '@sentry/nextjs';
import config from 'config';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { withTheme } from 'styled-components';

import { Router } from '@techstyle/next-routes';
import { AssetContainer } from '@techstyle/react-assets';
import { FormattedMessage } from '@techstyle/react-intl';

import { initCheckout } from '../../actions/checkout';
import { SignUpMethod } from '../../constants/auth';
import { MembershipActionLocation } from '../../constants/checkout';
import { SIGN_UP } from '../../constants/signIn';
import { BasicButton } from '../../styles/button';
import { Paragraph } from '../../styles/paragraph';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import { isProductSet, isSetOutOfStock } from '../../utils/selectors';
import { useAutoBundle2 } from '../../utils/useAutoBundle2';
import { useFreeShipping } from '../../utils/useFreeShipping';
import useGiftWithPurchase from '../../utils/useGiftWithPurchase';
import { useVipCTAStyling } from '../../utils/useVipCTAStyling';
import { Component as BorderfreeGuard } from '../BorderfreeGuard';
import { useByoContext } from '../ByoProvider';
import { Container as PromotionProgressBar } from '../PromotionProgressBar';
import StaticIcon from '../StaticIcon/StaticIcon';
import ToastContext from '../ToastContext/ToastContext';

import { CartDrawerCollapsableFooter } from './CartDrawerCollapsableFooter';
import { CartDrawerExpandedFooter } from './CartDrawerExpandedFooter';
import { CartDrawerRenderer } from './CartDrawerRenderer';
import { useHasItemExceedingStock } from './useHasItemExceedingStock';

const CustomParagraph = styled(Paragraph).attrs({
  size: 12,
  align: 'center',
})`
  ${props => props.theme.paragraph.variants.paragraph5Uppercase.textStyles}
  background-color: ${props => props.theme.colors.lavender200};
  color: ${props => props.theme.colors.gunmetal};
  padding: ${props =>
    `4px ${props.theme.spacing.tiny}px 4px ${props.theme.spacing.tiny}px`};
  margin-bottom: ${props => props.theme.spacing.tiny}px;
`;

const Footer = styled.div`
  position: relative;
  margin-top: auto;
  padding: 16px 20px 20px 20px;
  border-top: 1px solid ${props => props.theme.colors.stone};

  ${({ isRevampedShoppingBag }) =>
    isRevampedShoppingBag &&
    `
    padding: 0;
    width: 100%;
  `}
`;

const CheckoutButton = styled(BasicButton).attrs({
  fullWidth: true,
})`
  ${({ isRevampedShoppingBag }) =>
    isRevampedShoppingBag &&
    `
    padding: 0;
    width: 100%;
    span {
      line-height: 50px;
    }
  `}
`;

const PadlockIcon = styled(StaticIcon).attrs({
  type: 'padlock',
  size: 20,
})`
  transform: translateY(-4px);

  svg {
    fill: ${({ isVipCTAStyling, theme }) =>
      isVipCTAStyling ? theme.colors.black : theme.colors.white};
  }
`;

class CartDrawer extends Component {
  static propTypes = {
    accountMembershipItemInfo: PropTypes.object,
    addFirstByoItem: PropTypes.func.isRequired,
    addMembershipToCart: PropTypes.func.isRequired,
    allProducts: PropTypes.object,
    allowExclusiveCheckout: PropTypes.bool,
    allowOutOfStockCheckout: PropTypes.bool,
    assets: PropTypes.object,
    availableTokenQuantity: PropTypes.number,
    byoSetsForMostExpensiveItem: PropTypes.array.isRequired,
    cart: PropTypes.object.isRequired,
    containsBundle: PropTypes.bool.isRequired,
    deleteBasketItem: PropTypes.func.isRequired,
    deleteBasketItemsByProductId: PropTypes.func.isRequired,
    deleteMembershipFromCart: PropTypes.func.isRequired,
    deleteProductSet: PropTypes.func.isRequired,
    freeShippingThreshold: PropTypes.number.isRequired,
    gwpMinSubtotal: PropTypes.number,
    hasOnlyMembershipItem: PropTypes.bool,
    hasMembershipInCart: PropTypes.bool,
    hasOutOfStockItems: PropTypes.bool,
    increaseByoStep: PropTypes.func.isRequired,
    initiateByoFlow: PropTypes.func.isRequired,
    isAutoBundle2FeatureEnabled: PropTypes.bool,
    isBorderfreeCustomer: PropTypes.bool,
    isDowngradedMember: PropTypes.bool,
    isEu: PropTypes.bool.isRequired,
    isFlowInitiated: PropTypes.bool.isRequired,
    isFreeShippingFlagEnabled: PropTypes.bool,
    isGwpAvailable: PropTypes.bool,
    isInfluencer: PropTypes.bool,
    isLead: PropTypes.bool,
    isOpen: PropTypes.bool.isRequired,
    isPaygo: PropTypes.bool,
    isVip: PropTypes.bool,
    isVipCTAStyling: PropTypes.bool,
    // If the user is a visitor, show Sign In modal after clicking 'Checkout'
    isVisitor: PropTypes.bool,
    loadAssets: PropTypes.func,
    loadCart: PropTypes.func.isRequired,
    loadProfile: PropTypes.func,
    membershipItem: PropTypes.object,
    membershipItemInfo: PropTypes.object,
    mostExpensiveItem: PropTypes.array,
    onToggle: PropTypes.func.isRequired,
    products: PropTypes.array.isRequired,
    setByoGroupCode: PropTypes.func.isRequired,
    shouldDisplayGwp: PropTypes.bool,
    tld: PropTypes.string,
    toggleSignInModal: PropTypes.func,
    trackAddedItems: PropTypes.func.isRequired,
    trackByoInterstitialOpen: PropTypes.func.isRequired,
    updateItemQuantity: PropTypes.func.isRequired,
    updateSessionDetail: PropTypes.func,
    updateSetItemQuantity: PropTypes.func.isRequired,
    sweepCart: PropTypes.func.isRequired,
    isGuestCheckoutEnabled: PropTypes.bool,
    isRevampedShoppingBag: PropTypes.bool,
  };

  static defaultProps = {
    allowOutOfStockCheckout: false,
    hasOnlyMembershipItem: false,
  };

  async componentDidMount() {
    if (this.props.isOpen) {
      this.props.loadCart();
    }
    await this.props.loadAssets(['upsell_html_minicart']);
  }

  componentDidUpdate(prevProps, prevState) {
    // This will `return` immediately every time if profile is already loaded.
    const { loadProfile } = this.props;
    loadProfile();
  }

  renderAssetContainer = asset => {
    const {
      accountMembershipItemInfo,
      allowExclusiveCheckout,
      cart,
      freeShippingThreshold,
      isVip,
      membershipItem,
      membershipItemInfo,
      isFreeShippingFlagEnabled,
      isRevampedShoppingBag,
    } = this.props;

    if (isRevampedShoppingBag) {
      return null;
    }

    const hasMembershipItem = !!membershipItem;

    const activeMembershipInfo =
      accountMembershipItemInfo || membershipItemInfo;

    const hasMemberFreeShipping = activeMembershipInfo
      ? activeMembershipInfo.freeShipping
      : false;

    const shouldShowProgressBar =
      !hasMemberFreeShipping && !allowExclusiveCheckout;

    let hasFreeShipping = false;

    if (isVip) {
      hasFreeShipping = !!asset || hasMemberFreeShipping;
    } else {
      hasFreeShipping = !!asset && hasMembershipItem;
    }

    if (isFreeShippingFlagEnabled && (hasFreeShipping || !hasFreeShipping)) {
      return (
        <CustomParagraph>
          <FormattedMessage
            id="global_checkout.free_shipping_congratulation"
            defaultMessage="Congrats! You get Free Shipping."
          />
        </CustomParagraph>
      );
    } else if (!hasFreeShipping || !isFreeShippingFlagEnabled) {
      return (
        <BorderfreeGuard>
          {shouldShowProgressBar || !isFreeShippingFlagEnabled ? (
            <PromotionProgressBar
              total={cart.subtotalMinusDiscount}
              promoThreshold={freeShippingThreshold}
              promoType="freeShipping"
            />
          ) : null}
        </BorderfreeGuard>
      );
    } else {
      return null;
    }
  };

  signInBeforeCheckout = showMessage => event => {
    const {
      isVisitor,
      updateSessionDetail,
      products,
      toggleSignInModal,
      isGuestCheckoutEnabled,
      isRevampedShoppingBag,
    } = this.props;

    if (isVisitor && !isGuestCheckoutEnabled) {
      updateSessionDetail({
        name: 'signup_minicart',
        value: '',
      });

      const product = [...products].sort((a, b) => a.lineId - b.lineId)[
        products.length - 1
      ];
      const isSet = isProductSet(product);
      toggleSignInModal(
        true,
        SIGN_UP,
        SignUpMethod.CARTDRAWER,
        product.masterProductId,
        isSet
      ).then(data => {
        if (data && data.signedIn) {
          this.continueCheckout(true);
        } else if (!isRevampedShoppingBag) {
          showMessage(
            <FormattedMessage
              id="global_checkout.sign_in_error_message"
              defaultMessage="Please sign in or sign up to continue checkout."
            />
          );
        }
      });
    } else {
      // Already logged in
      this.continueCheckout();
    }
  };

  continueCheckout = async isAfterSignIn => {
    const {
      addFirstByoItem,
      availableTokenQuantity,
      byoSetsForMostExpensiveItem,
      containsBundle,
      deleteBasketItem,
      dispatch,
      increaseByoStep,
      initiateByoFlow,
      isAutoBundle2FeatureEnabled,
      isFlowInitiated,
      isRevampedShoppingBag,
      isVip,
      mostExpensiveItem,
      onToggle,
      profile,
      setByoGroupCode,
      tld,
      trackAddedItems,
      trackByoInterstitialOpen,
      sweepCart,
    } = this.props;
    onToggle(false);

    const twoItemBundleByoProductId = config.get(
      `public.byo.twoItemBundleByoProductId`
    )[tld];

    const twoItemBundle =
      byoSetsForMostExpensiveItem.find(
        byoSet => byoSet.byoProductId === twoItemBundleByoProductId
      ) || null;

    const doInterstitialLogic = async () => {
      try {
        const result = await addFirstByoItem(
          mostExpensiveItem.productId,
          twoItemBundle.byoProductId,
          1
        );
        if (result?.payload?.lastByoGroupCode) {
          await deleteBasketItem(
            mostExpensiveItem.cartLineId,
            MembershipActionLocation.MINI_CART
          );

          const byoGridUrl = twoItemBundle.combinationOptions[0]?.byoGridsUrl;

          initiateByoFlow({
            byoSet: twoItemBundle,
            isInterstitialFlow: true,
            firstProductMpid: mostExpensiveItem.masterProductId,
          });

          setByoGroupCode(result.payload.lastByoGroupCode);
          trackAddedItems(mostExpensiveItem);
          increaseByoStep();
          trackByoInterstitialOpen({
            masterProductId: mostExpensiveItem.masterProductId,
            profile,
          });
          Router.push(
            `${byoGridUrl}?lastMasterProductId=${mostExpensiveItem.masterProductId}&byoInterstitial=true`
          );
        }
      } catch (e) {
        captureException(e);
        Router.push('/cart');
      }
    };

    // If AutoBundle2 Is On and they meet the Reqs do Sweep
    if (availableTokenQuantity > 0 && isVip && isAutoBundle2FeatureEnabled) {
      try {
        const result = await sweepCart();
        const itemsFromSweep = result?.payload?.items;
        const sweepContainsBundles = itemsFromSweep.some(
          item => item.bundleItems?.length > 0
        );
        if (
          !sweepContainsBundles &&
          !isFlowInitiated &&
          twoItemBundle !== null
        ) {
          await doInterstitialLogic();
        }
      } catch (e) {
        captureException(e);
      }
    }

    if (
      availableTokenQuantity > 0 &&
      !containsBundle &&
      !isFlowInitiated &&
      twoItemBundle !== null &&
      !isAutoBundle2FeatureEnabled
    ) {
      await doInterstitialLogic();
    } else {
      // FIXME: For some reason making the cart load twice fixes an issue where incorrect pricing data
      // is displayed in the cart. After the first load of the cart the information is incorrect, but it
      // is correct after the second load. This is a temporary fix until there is an API fix.
      // The second load happens on the cart page, see `pages/cart.js`
      if (isAfterSignIn) {
        await this.props.loadCart();
      }
      if (isRevampedShoppingBag) {
        try {
          // on revamped shopping bag, we need to go straight to checkout from the cart drawer
          await dispatch(initCheckout());
        } catch (e) {
          captureException(e);
          Router.push('/cart');
        }
      } else {
        Router.push('/cart');
      }
    }
  };

  render() {
    const {
      allowOutOfStockCheckout,
      getNewDate,
      hasNonPreorderItems,
      hasOnlyMembershipItem,
      hasOutOfStockItems,
      isBorderfreeCustomer,
      isRevampedShoppingBag,
      isVipCTAStyling,
      membershipItem,
      hasItemExceedingStock,
      products,
      cart,
    } = this.props;

    const borderfreeCustomerWithMembership =
      isBorderfreeCustomer && membershipItem;

    let showCartFooter = true;

    const cartHasItems =
      cart.items.length === 1 && !!membershipItem
        ? false
        : cart.items.length > 0;
    if (!isBorderfreeCustomer && !hasNonPreorderItems && !cartHasItems) {
      showCartFooter = false;
    }
    const disableCheckoutButton =
      hasOnlyMembershipItem ||
      (hasOutOfStockItems && !allowOutOfStockCheckout) ||
      borderfreeCustomerWithMembership ||
      hasItemExceedingStock;

    const showOutOfStockLabel = isRevampedShoppingBag
      ? hasOutOfStockItems ||
        products?.some(product => isSetOutOfStock(product, getNewDate))
      : false;
    const checkoutButton = isBorderfreeCustomer ? (
      <CheckoutButton
        onClick={this.continueCheckout}
        disabled={disableCheckoutButton}
        alternate={isVipCTAStyling}
        isRevampedShoppingBag={isRevampedShoppingBag}
        role="button"
      >
        {isRevampedShoppingBag ? (
          <>
            <PadlockIcon isVipCTAStyling />
            <FormattedMessage
              id="global_checkout.checkout_securely_cta"
              defaultMessage="Checkout securely"
            />
          </>
        ) : (
          <FormattedMessage
            id="global_checkout.checkout_cta"
            defaultMessage="Checkout"
          />
        )}
      </CheckoutButton>
    ) : (
      <ToastContext.Consumer>
        {({ showMessage }) => (
          <CheckoutButton
            onClick={this.signInBeforeCheckout(showMessage)}
            disabled={disableCheckoutButton}
            isRevampedShoppingBag={isRevampedShoppingBag}
            role="button"
            alternate={isVipCTAStyling}
            data-autotag="mini-cart-checkout-btn"
          >
            {isRevampedShoppingBag ? (
              <>
                <PadlockIcon />
                <FormattedMessage
                  id="global_checkout.checkout_securely_cta"
                  defaultMessage="Checkout securely"
                />
              </>
            ) : (
              <FormattedMessage
                id="global_checkout.checkout_cta"
                defaultMessage="Checkout"
              />
            )}
          </CheckoutButton>
        )}
      </ToastContext.Consumer>
    );

    const collapsableFooter = showCartFooter && (
      <CartDrawerCollapsableFooter
        checkoutButton={checkoutButton}
        hasMembershipItem={!!membershipItem}
        showOutOfStockLabel={showOutOfStockLabel}
      />
    );
    const expandedFooter = showCartFooter && (
      <CartDrawerExpandedFooter
        checkoutButton={checkoutButton}
        hasMembershipItem={!!membershipItem}
        showOutOfStockLabel={showOutOfStockLabel}
      />
    );

    const footer = showCartFooter && (
      <Footer>
        <AssetContainer name="mini_cart_shipping">
          {asset => this.renderAssetContainer(asset.data)}
        </AssetContainer>
        {checkoutButton}
      </Footer>
    );
    return (
      <CartDrawerRenderer
        footer={footer}
        collapsableFooter={collapsableFooter}
        expandedFooter={expandedFooter}
      />
    );
  }
}

function CartDrawerWithHooks(props) {
  const { isGwpAvailable, gwpMinSubtotal, shouldDisplayGwp, isGwpApplied } =
    useGiftWithPurchase();
  const { isControl } = useLDNewShoppingBagUX();
  const { 'guest-checkout': isGuestCheckoutEnabled } = useLDFlags();

  const freeShippingEnabled = useFreeShipping(props.isOpen);
  const dispatch = useDispatch();
  const autoBundle2Enabled = useAutoBundle2();

  const {
    initiateByoFlow,
    setByoGroupCode,
    increaseByoStep,
    trackAddedItems,
    isFlowInitiated,
  } = useByoContext();

  const isVipCTAStyling = useVipCTAStyling();
  const hasItemExceedingStock = useHasItemExceedingStock();

  return (
    <CartDrawer
      {...props}
      isAutoBundle2FeatureEnabled={autoBundle2Enabled}
      isGwpAvailable={isGwpApplied ? false : isGwpAvailable}
      gwpMinSubtotal={gwpMinSubtotal}
      shouldDisplayGwp={shouldDisplayGwp}
      initiateByoFlow={initiateByoFlow}
      setByoGroupCode={setByoGroupCode}
      increaseByoStep={increaseByoStep}
      trackAddedItems={trackAddedItems}
      isFlowInitiated={isFlowInitiated}
      isFreeShippingFlagEnabled={freeShippingEnabled}
      isVipCTAStyling={isVipCTAStyling}
      isGuestCheckoutEnabled={isGuestCheckoutEnabled}
      isRevampedShoppingBag={!isControl}
      hasItemExceedingStock={!isControl && hasItemExceedingStock}
      dispatch={dispatch}
    />
  );
}

export default withTheme(CartDrawerWithHooks);
